import { HTMLAttributes, useEffect, useMemo, useState } from 'react';
import { promotionInfoStore } from '@/website/store/promotion/promotionInfoStore';
import { ExpandLess, ExpandMore, InfoOutlined } from '@mui/icons-material';
import { Box, Button, Collapse, Typography } from '@mui/material';
import clsx from 'clsx';

type PartnerStatementConfig = {
  enabled?: boolean;
  title?: string;
  paragraphs?: Array<{
    text?: string;
  }>;
  backgroundColor?: string;
  textColor?: string;
  fontSize?: string;
  defaultExpanded?: boolean;
};

const DEFAULT_CONFIG: Required<PartnerStatementConfig> = {
  enabled: false,
  // title: 'Insurance partnership disclosure',
  title: '',
  paragraphs: [],
  backgroundColor: '#FFFFFF',
  textColor: '#202020',
  fontSize: '16px',
  defaultExpanded: false,
};

type PartnerStatementProps = HTMLAttributes<HTMLDivElement> & {};
const PartnerStatement = (props: PartnerStatementProps) => {
  const { className } = props;
  const [isExpanded, setIsExpanded] = useState(DEFAULT_CONFIG.defaultExpanded);
  const {
    state: { promotionInfo },
  } = promotionInfoStore();

  const mergedConfig: Required<PartnerStatementConfig> = useMemo(() => {
    const cmsConfig = promotionInfo?.payload?.website_configuration?.['partner_statement'];
    const cleanedParagraphs = (cmsConfig?.paragraph_list || DEFAULT_CONFIG.paragraphs)
      .map((item) => ({ text: item?.text || '' }))
      .filter((item) => !!item.text?.trim());
    return {
      enabled: cmsConfig?.enabled ?? DEFAULT_CONFIG.enabled,
      title: cmsConfig?.title || DEFAULT_CONFIG.title,
      paragraphs: cleanedParagraphs,
      backgroundColor: cmsConfig?.background_color || DEFAULT_CONFIG.backgroundColor,
      textColor: cmsConfig?.text_color || DEFAULT_CONFIG.textColor,
      fontSize: cmsConfig?.font_size || DEFAULT_CONFIG.fontSize,
      defaultExpanded: cmsConfig?.['default_expanded'] ?? DEFAULT_CONFIG.defaultExpanded,
    };
  }, [promotionInfo]);

  useEffect(() => {
    setIsExpanded(mergedConfig.defaultExpanded);
  }, [mergedConfig.defaultExpanded]);

  if (!mergedConfig?.enabled) {
    return null;
  }

  return (
    <Box className={clsx('flex w-full justify-center px-0', className)}>
      <Box
        className="flex w-full flex-col overflow-hidden shadow-[0_10px_40px_rgba(0,0,0,0.05)] ring-1 ring-[#EBEFFE]"
        style={{ backgroundColor: mergedConfig.backgroundColor }}
      >
        <Button
          onClick={() => setIsExpanded((prev) => !prev)}
          endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
          sx={{
            justifyContent: 'space-between',
            padding: '16px 24px',
            textTransform: 'none',
            fontWeight: 700,
            color: mergedConfig.textColor,
            borderRadius: 0,
          }}
          aria-expanded={isExpanded}
          aria-controls="partner-statement-content"
        >
          <Box className="flex items-center space-x-3">
            <Box className="text-primary-main flex h-10 w-10 items-center justify-center rounded bg-[#EBEFFE]">
              <InfoOutlined color="primary" />
            </Box>
            <Typography component="h2" variant="subtitle1" className="text-primary-black font-semibold" sx={{ color: mergedConfig.textColor }}>
              {mergedConfig.title}
            </Typography>
          </Box>
        </Button>
        <Collapse in={isExpanded} timeout="auto">
          <Box id="partner-statement-content" className="space-y-3 px-6 pb-6" sx={{ paddingTop: 0 }}>
            {mergedConfig.paragraphs.map((paragraph, index) => (
              <Typography
                key={index}
                component="p"
                className="font-bold leading-6"
                sx={{ color: mergedConfig.textColor, fontSize: mergedConfig.fontSize }}
              >
                {paragraph.text}
              </Typography>
            ))}
          </Box>
        </Collapse>
        {isExpanded && <Box className="h-px w-full bg-[#E5E7EB]" />}
      </Box>
    </Box>
  );
};

export default PartnerStatement;
